import { convertSearchParamsToObject } from "helpers";
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Button, RangeFilterField } from "ui";

import { useGetOrdersFilterLimits } from "../../../apis";
import {
  EMPTY_ORDERS_FILTER_FORM_VALUES,
  ORDER_FILTER_SEARCH_PARAMETERS,
  TOTAL_FROM_FILTER_SEARCH_PARAMETER,
  TOTAL_TO_FILTER_SEARCH_PARAMETER
} from "../../../constants";
import {
  DateRangeBlock,
  DriverBlock,
  GenderBlock,
  LocationBlock,
  OrderTypeBlock,
  StatusBlock,
  TextInputBlock,
  UnpackingStatusBlock
} from "./FormBlocks";
import {
  OrdersFilterFormProps,
  FilterFormValues
} from "./OrdersFilterForm.types";

import styles from "./OrdersFilterForm.module.scss";

const OrdersFilterForm = ({
  formId,
  onSubmit,
  isLoading,
  drivers,
  isDriversLoading,
  locations,
  isLocationsLoading
}: OrdersFilterFormProps) => {
  const { t } = useTranslation();
  const { filterLimits, isLoading: isFilterLimitsLoading } =
    useGetOrdersFilterLimits();
  const [searchParams, setSearchParams] = useSearchParams();

  const formMethods = useForm<FilterFormValues>({
    defaultValues: EMPTY_ORDERS_FILTER_FORM_VALUES
  });

  const { handleSubmit, reset } = formMethods;

  useEffect(() => {
    const newValues = convertSearchParamsToObject(searchParams);

    if (newValues?.statuses && !Array.isArray(newValues.statuses)) {
      newValues.statuses = [newValues.statuses];
    }

    const handledNewValues = {
      ...EMPTY_ORDERS_FILTER_FORM_VALUES,
      ...newValues
    };

    reset(handledNewValues);
  }, [searchParams]);

  const clearFilterForm = () => {
    for (const key of ORDER_FILTER_SEARCH_PARAMETERS) {
      searchParams.delete(key);
    }

    setSearchParams(searchParams);
    reset();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        id={formId}
        className={styles.orderFilterForm}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.orderFilterForm__header}>
          <div className={styles.orderFilterForm__headerTitle}>
            {t("Filter by")}
          </div>
          <div
            onClick={clearFilterForm}
            className={styles.orderFilterForm__headerClear}
          >
            {t("Clear all filters")}
          </div>
        </div>

        <OrderTypeBlock />

        <div className={styles.orderFilterForm__line} />

        <GenderBlock />

        <div className={styles.orderFilterForm__line} />

        {filterLimits && (
          <RangeFilterField
            title={`${t("Order Total")} (FCFA)`}
            fromFieldKey={TOTAL_FROM_FILTER_SEARCH_PARAMETER}
            toFieldKey={TOTAL_TO_FILTER_SEARCH_PARAMETER}
            minTotal={Math.floor(filterLimits.minOrderTotal)}
            maxTotal={Math.ceil(filterLimits.maxOrderTotal)}
          />
        )}

        {isFilterLimitsLoading && (
          <div className={styles.orderFilterForm__loader}>
            <ClipLoader size={50} color="#fb3973" />
          </div>
        )}

        <div className={styles.orderFilterForm__line} />

        <DriverBlock drivers={drivers} isDriversLoading={isDriversLoading} />

        <div className={styles.orderFilterForm__line} />

        <LocationBlock
          locations={locations}
          isLocationsLoading={isLocationsLoading}
        />

        <div className={styles.orderFilterForm__line} />

        <TextInputBlock
          name="clientName"
          title="Client name"
          placeholder="Client name"
        />

        <div className={styles.orderFilterForm__line} />

        <TextInputBlock
          name="clientPhone"
          title="Client phone"
          placeholder="Client phone"
        />

        <div className={styles.orderFilterForm__line} />

        <TextInputBlock
          name="resellerName"
          title="Reseller name"
          placeholder="Reseller name"
        />

        <div className={styles.orderFilterForm__line} />

        <TextInputBlock
          name="resellerPhone"
          title="Reseller phone"
          placeholder="Reseller phone"
        />

        <div className={styles.orderFilterForm__line} />

        <TextInputBlock
          name="deliveryLocation"
          title="Delivery location"
          placeholder="Delivery location"
        />

        <div className={styles.orderFilterForm__line} />

        <DateRangeBlock />

        <div className={styles.orderFilterForm__line} />

        <UnpackingStatusBlock />

        <div className={styles.orderFilterForm__line} />

        <StatusBlock />

        <div className={styles.orderFilterForm__line} />
        <Button
          type="submit"
          className={styles.orderFilterForm__button}
          loading={isLoading}
        >
          {t("Apply")}
        </Button>
      </form>
    </FormProvider>
  );
};

export default OrdersFilterForm;
